import { useTranslation } from "@redotech/redo-i18n";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import LockIcon from "@redotech/redo-icons/lock-01.svg";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Team } from "@redotech/redo-team-common/team";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { Handle, Node, NodeProps, Position, useViewport } from "@xyflow/react";
import classNames from "classnames";
import { memo, useMemo } from "react";
import {
  localizeFlowSidebarTitle,
  localizeFlowStepTitle,
} from "../utils/localized-step-title";
import * as styles from "./flow-step-node.module.css";

const NODE_WIDTH = 224;
const HANDLE_VISUAL_SIZE = 8;
const HANDLE_HOVER_SIZE = 32;
export const ZOOM_THRESHOLD_COMBINE = 0.75;

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type NodeData = {
  step: Step;
  stepId: StepId;
  flowType: FlowType;
  nodePositions: Map<string, { x: number; y: number }>;
  allSteps: Map<string, Step>;
  team?: Team;
  pathHighlighted?: boolean;
  isCurrentStep?: boolean;
  warningMessage?: string;
  redoManaged?: boolean;
};

export const FlowStepNode = memo(function FlowStepNode({
  data,
  selected: nodeSelected,
}: NodeProps<Node<NodeData>>) {
  const { t } = useTranslation();
  const {
    step,
    stepId,
    flowType,
    nodePositions,
    allSteps,
    team,
    pathHighlighted,
    isCurrentStep,
    warningMessage,
    redoManaged,
  } = data;

  const { zoom } = useViewport();

  const selected = nodeSelected;
  const layout = step.layout();
  const isCompact = layout === BlockLayout.COMPACT;
  const nodeWidth = NODE_WIDTH;
  const downstreams = step.downstream();
  const maxConnections = step.maxDownstream();
  const canBeTarget = stepId !== "start";

  const shouldCombineHandles =
    zoom < ZOOM_THRESHOLD_COMBINE && maxConnections > 2;

  const downstreamBySemanticIndex = useMemo(() => {
    if (!nodePositions || !allSteps) {
      return new Map(
        downstreams.map((downstream, index) => [index, downstream]),
      );
    }

    return new Map(
      downstreams
        .map((downstream, index) => ({ downstream, index }))
        .filter(
          ({ downstream }) => downstream.id && allSteps.has(downstream.id),
        )
        .map(({ downstream, index }) => [index, downstream]),
    );
  }, [downstreams, nodePositions, allSteps]);

  const sidebarTitle = step.sidebarTitle?.();
  const stepTitle = step.title();
  const displayedTitle =
    sidebarTitle == null
      ? localizeFlowStepTitle(stepTitle)
      : localizeFlowSidebarTitle(sidebarTitle, stepTitle);

  return (
    <>
      {canBeTarget && (
        /* design-allow: React Flow handle geometry requires runtime styles. */
        <Handle
          id={`${stepId}-target`}
          position={Position.Top}
          // design-allow: preserve React Flow handle geometry.
          style={{
            position: "absolute",
            top: "-28px",
            left: "50%",
            transform: "translateX(-50%)",
            width: `${HANDLE_HOVER_SIZE}px`,
            height: `${HANDLE_HOVER_SIZE}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            background: "transparent",
            border: "none",
            opacity: 0,
            cursor: "crosshair",
          }}
          type="target"
        />
      )}

      <Flex
        className={classNames(
          styles.nodeCard,
          {
            "border border-red-500": !step.valid(flowType),
            "border border-gray-700": selected,
            "border border-gray-300": !selected && step.valid(flowType),
          },
          pathHighlighted && styles.pathHighlighted,
          !pathHighlighted && isCurrentStep && styles.currentStep,
        )}
        dir="column"
        gap="md"
        style={{
          width: stepId === "start" ? "unset" : nodeWidth,
          maxWidth: nodeWidth,
        }}
      >
        <Flex align="center" gap="sm" justify="space-between">
          <Flex
            align="center"
            flex={1}
            gap="sm"
            justify="flex-start"
            minW="none"
          >
            <RedoFeaturedIcon
              color={stepId === "start" ? "black" : step.iconColor || "warning"}
              Icon={step.Icon}
              size="xs"
              type={stepId === "start" ? "high-contrast" : "default"}
            />
            <Flex grow={1} minW="none">
              {/* design-allow: preserve flow node title wrapping. */}
              <Text
                fontSize="sm"
                fontWeight="medium"
                minW="none"
                // design-allow: preserve flow node title wrapping.
                style={{ wordBreak: "break-word" }}
              >
                {displayedTitle}
              </Text>
            </Flex>
          </Flex>
          {warningMessage && (
            <Tooltip title={warningMessage}>
              <Flex
                align="center"
                justify="center"
                shrink={0}
                textColor="error"
              >
                <InfoCircleIcon height={16} width={16} />
              </Flex>
            </Tooltip>
          )}
          {redoManaged && (
            <Tooltip
              title={t(
                "This step is managed by {{brandName}} and can't be reconfigured.",
                { brandName: "Redo" },
              )}
            >
              <Flex
                align="center"
                justify="center"
                shrink={0}
                textColor="secondary"
              >
                <LockIcon height={16} width={16} />
              </Flex>
            </Tooltip>
          )}
        </Flex>

        {stepId !== "start" &&
          (() => {
            const richContent = step.descriptionContent?.(team, flowType);
            if (richContent != null) {
              return richContent;
            }
            return (
              /* design-allow: preserve flow step description truncation styles. */
              <Text
                // design-allow: preserve flow step description truncation styles.
                style={{
                  fontSize: "var(--redo-font-size-xs)",
                  wordBreak: "break-word",
                  lineHeight: "var(--redo-line-height-xs)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: isCompact ? 2 : 3,
                  WebkitBoxOrient: "vertical",
                }}
                textColor="tertiary"
              >
                {step.description(team, flowType)}
              </Text>
            );
          })()}

        {!step.valid(flowType) && (
          <Text fontSize="xs" fontWeight="medium" mt="xs" textColor="error">
            {t("Invalid configuration")}
          </Text>
        )}
      </Flex>

      {Array.from({ length: maxConnections }, (_, handleIndex) => {
        const spacing = maxConnections > 1 ? 0.8 / (maxConnections - 1) : 0;
        const startOffset = maxConnections > 1 ? 0.1 : 0.5;
        const individualPosition =
          maxConnections === 1
            ? "50%"
            : `${(startOffset + spacing * handleIndex) * 100}%`;

        const leftPosition = shouldCombineHandles ? "50%" : individualPosition;

        const correspondingDownstream =
          downstreamBySemanticIndex.get(handleIndex);
        const handleId = correspondingDownstream?.id
          ? `${stepId}-${correspondingDownstream.id}-${handleIndex}`
          : `${stepId}-placeholder-${handleIndex}`;

        const showVisualHandle = !shouldCombineHandles;

        return (
          /* design-allow: React Flow handle geometry requires runtime styles. */
          <Handle
            id={handleId}
            key={handleId}
            position={Position.Bottom}
            // design-allow: preserve React Flow handle geometry.
            style={{
              position: "absolute",
              bottom: "-28px",
              left: leftPosition,
              transform: "translateX(-50%)",
              width: `${HANDLE_HOVER_SIZE}px`,
              height: `${HANDLE_HOVER_SIZE}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              background: "transparent",
              border: "none",
              opacity: showVisualHandle ? 1 : 0,
              pointerEvents: showVisualHandle ? "auto" : "none",
              cursor: "crosshair",
            }}
            type="source"
          >
            {/* design-allow: handle marker geometry requires runtime styles. */}
            <div
              // design-allow: preserve React Flow handle marker geometry.
              style={{
                background: "var(--redo-colors-background-bg-primary)",
                width: `${HANDLE_VISUAL_SIZE}px`,
                height: `${HANDLE_VISUAL_SIZE}px`,
                border: "2px solid black",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                borderRadius: "var(--border-radius-full)",
                pointerEvents: "none",
              }}
            />
          </Handle>
        );
      })}

      {shouldCombineHandles && (
        /* design-allow: React Flow combined handle geometry requires a div. */
        <div
          // design-allow: preserve React Flow combined handle geometry.
          style={{
            position: "absolute",
            bottom: "-28px",
            left: "50%",
            transform: "translateX(-50%)",
            width: `${HANDLE_HOVER_SIZE}px`,
            height: `${HANDLE_HOVER_SIZE}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {/* design-allow: handle marker geometry requires runtime styles. */}
          <div
            // design-allow: preserve React Flow handle marker geometry.
            style={{
              background: "var(--redo-colors-background-bg-primary)",
              width: `${HANDLE_VISUAL_SIZE}px`,
              height: `${HANDLE_VISUAL_SIZE}px`,
              border: "2px solid black",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "var(--border-radius-full)",
            }}
          />
        </div>
      )}
    </>
  );
});
